import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Stack,
  Typography,
  BoxProps,
  CardActionAreaProps,
  SxProps,
  Theme,
  TypographyProps,
} from '@mui/material';
// ICONS
// import PauseCircleIcon from '@mui/icons-material/PauseCircle';
// CONSTANTS
import { TP_COLORS } from '../../../../constants';
// HOOKS
import { useAppSelector } from '../../../../hooks';
// STORE
import { getThemeMode } from '../../../../store/ConfigSlice';

interface CountdownTimerProps {
  value: number;
  format?: 'hh:mm:ss' | 'mm:ss';
  gridFontSize?: React.CSSProperties['fontSize'];
  dividerFontSize?: React.CSSProperties['fontSize'];
  gridItemStyles?: BoxProps['sx'];
  actionsProps?: CardActionAreaProps;
  noCountDown?: boolean;
  behavior?: 'increment' | 'decrement';
  isExpired?: boolean;
  showSubmissionTimeUpAlert?: () => void | undefined;
  isTimeUpAlertVisible?: boolean;
}

interface IState {
  seconds: number;
}

// STYLES
export const defaultGridItemStyles: BoxProps['sx'] = {
  backgroundColor: TP_COLORS.black,
  color: TP_COLORS.white,
  borderRadius: '4px',
  padding: '3px 7px',
};

// const overlayStyles: React.CSSProperties = {
//   position: 'absolute',
//   backgroundColor: 'rgba(0,0,0,0.8)',
//   width: '100%',
//   height: '100%',
//   color: TP_COLORS.white,
//   top: 0,
//   left: 0,
//   bottom: 0,
//   right: 0,
//   zIndex: 2,
//   display: 'flex',
//   justifyContent: 'center',
//   alignItems: 'center',
// };

// DATA
const initialState: IState = {
  seconds: 0,
};

const CountdownTimer: React.FC<CountdownTimerProps> = ({
  value,
  format = 'hh:mm:ss',
  gridFontSize = '16px',
  dividerFontSize = '30px',
  gridItemStyles = defaultGridItemStyles,
  actionsProps = { disabled: true },
  noCountDown = false,
  behavior = 'decrement',
  isExpired = false,
  showSubmissionTimeUpAlert = undefined,
  isTimeUpAlertVisible = false,
}) => {
  // STORE
  const themeMode = useAppSelector(getThemeMode);

  // STATE
  const [state, setState] = useState<IState>(initialState);
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout | undefined>(
    undefined
  );

  // USE EFFECT
  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      seconds: value || 0,
    }));

    if (!noCountDown) {
      const interval = setInterval(() => {
        setState((prevState) => {
          if (behavior === 'increment') {
            return {
              ...prevState,
              seconds: prevState.seconds + 1,
            };
          }
          return {
            ...prevState,
            seconds: prevState.seconds - 1,
          };
        });
      }, 1000);
      setIntervalId(interval);
    }

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (noCountDown) {
      clearInterval(intervalId);
    }
  }, [noCountDown]);

  useEffect(() => {
    if (noCountDown) {
      setState({
        ...initialState,
        seconds: value,
      });
    }
  }, [value]);

  useEffect(() => {
    if (
      state.seconds < 0 &&
      behavior === 'decrement' &&
      showSubmissionTimeUpAlert &&
      !isTimeUpAlertVisible
    ) {
      showSubmissionTimeUpAlert();
    }
  }, [state.seconds]);

  let _hours = 0;
  let _minutes = 0;
  let _seconds = 0;

  const val = Math.abs(state.seconds);

  switch (format) {
    case 'hh:mm:ss':
      _hours = Math.floor(val / 3600);
      _minutes = Math.floor((val % 3600) / 60);
      _seconds = Math.floor((val % 3600) % 60);
      break;

    case 'mm:ss':
      _minutes = Math.floor(val / 60);
      _seconds = Math.floor(val % 60);
      break;

    default:
      _hours = Math.floor(val / 3600);
      _minutes = Math.floor((val % 3600) / 60);
      _seconds = Math.floor((val % 3600) % 60);
      break;
  }

  let _redBackgroundColor = 'error.light';
  const _redColor = '#f32b2d';

  let _backgroundColor = '#EBF9EB';
  let _color = '#56C955';

  // Quize timer reached to 1 minute
  if (state.seconds < 60) {
    _backgroundColor = '#FEF8E8';
    _color = '#CBA436';
  }

  if (themeMode === 'dark') {
    _redBackgroundColor = '#522b2f';
    _backgroundColor = 'background.default';
  }

  const _gridItemStyles: SxProps<Theme> = {
    ...gridItemStyles,
    backgroundColor: isExpired ? _redBackgroundColor : _backgroundColor,
    color: isExpired ? _redColor : _color,
  };

  const _dividerProps: TypographyProps = {
    fontSize: dividerFontSize,
    fontWeight: 600,
    color: isExpired ? _redColor : _color,
  };

  return (
    <Card
      elevation={0}
      style={{
        padding: 0,
        backgroundColor: 'transparent',
        width: 'fit-content',
        position: 'relative',
      }}
    >
      <CardActionArea {...actionsProps}>
        <CardContent style={{ padding: 0 }}>
          <Stack direction="row" alignItems="center">
            {/* HOURS */}
            {format === 'hh:mm:ss' && (
              <>
                <Box sx={_gridItemStyles}>
                  <Typography fontSize={gridFontSize}>
                    {_hours.toString().padStart(2, '0')}
                  </Typography>
                </Box>
                <Box style={{ margin: '0 5px' }}>
                  <Typography {..._dividerProps}>:</Typography>
                </Box>
              </>
            )}

            {/* HOURS */}
            <Box sx={_gridItemStyles}>
              <Typography fontSize={gridFontSize}>
                {_minutes.toString().padStart(2, '0')}
              </Typography>
            </Box>
            <Box style={{ margin: '0 5px' }}>
              <Typography {..._dividerProps}>:</Typography>
            </Box>

            {/* SECONDS */}
            <Box sx={_gridItemStyles}>
              <Typography fontSize={gridFontSize}>
                {_seconds.toString().padStart(2, '0')}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </CardActionArea>
      {/* <div style={overlayStyles}>
        <Typography variant="subtitle2" fontWeight={500}>
          Pause
        </Typography>
        <PauseCircleIcon
          style={{
            color: overlayStyles.color,
            width: '15px',
            height: '15px',
            marginLeft: '5px',
            fontSize: gridFontSize,
          }}
        />
      </div> */}
    </Card>
  );
};

export default CountdownTimer;
